import { useEffect, useState } from "react";
import CompanySetupLayout from "../../../components/layouts/CompanySetupLayout";
import { APP_CONFIG, moneyFormat } from "../../../helpers/appHelpers";
import { IoChevronBackOutline } from "react-icons/io5";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import Label from "../../../components/form_fields/Label";
import OrangeButton from "../../../components/buttons/OrangeButton";
import { DetailedSubscriptionTierDto, SubscriptionTierOrderBy } from "../../../models/subscription-tier.dto";
import { useGetSubscriptionTierPackages } from "../../../services/subscription-tier.service";
import { PATHS } from "../../../routes/routes.paths";
import { PERMISSIONS, useValidateUserPermissions } from "../../../helpers/usePermissionManager";
import { useGetPaymentInstitution } from "../../../services/payment-institution.service";
import { PaymentInstitutionOrderBy, PAYMENT_CHANNEL_TYPE } from "../../../models/payment-institution.dto";
import useUserStore from "../../../state-management/useUserStore";
import { isEditable } from "@testing-library/user-event/dist/utils";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import { InvoicePaymentDto } from "../../../models/invoice.dto";
import { useInitiatePayment } from "../../../services/invoice.service";

const SubscriptionPayment = () => {
	const hasPermission = useValidateUserPermissions(
		[PERMISSIONS.SUBSCRIPTIONS_WRITE], 
		"OR",
		PATHS.OVERVIEW
	);
	const navigate = useNavigate();
	const { currentUser } = useUserStore();
	const userOrganization = currentUser.user.organization;
	const [searchParams] = useSearchParams();
	const reference = searchParams.get("reference");
	const subscriptionTierId = searchParams.get("subscriptionTierId");
	const subscriptionStatus = searchParams.get("subscriptionStatus");
	const [subscriptionTier, setSubscriptionTier] = 
		useState<DetailedSubscriptionTierDto | undefined>(undefined);

	const {
		register,
		handleSubmit,
		control,
		watch,
		setValue,
		formState: { errors },
	} = useForm<InvoicePaymentDto>({});
		
	const { 
		data: subscriptionTierPackages, 
		isLoading: isLoadingSubscriptions
	} = useGetSubscriptionTierPackages({
		detailed: true,
		page: 1,
		limit: 20,
		orderBy: SubscriptionTierOrderBy.PRICE_AMOUNT_ASC,
	});
	
	const channelCode = watch("channelCode");
	const isCardPayment = channelCode === "CARD";

	// Card payments pick from bank institutions, mobile money from wallets
	const { data: paymentInstitutions } = useGetPaymentInstitution({
		active: true,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: PaymentInstitutionOrderBy.NAME_ASC,
		paymentChannelType: isCardPayment
			? PAYMENT_CHANNEL_TYPE.BANK
			: PAYMENT_CHANNEL_TYPE.WALLET,
		countryId: userOrganization!.countryId,
	});

	// The selected network belongs to the previous channel's institution list
	useEffect(() => {
		setValue("network", "");
	}, [channelCode]);

	useEffect(() => {
		if (isLoadingSubscriptions || !subscriptionTierId) return;

		setSubscriptionTier(
			subscriptionTierPackages?.data.find(
				subscriptionTier => subscriptionTier.id === subscriptionTierId
			)
		);
	}, [subscriptionTierId, isLoadingSubscriptions])
	
	const [uploading, setUploading] = useState(false);
	const { mutate: initiatePayment } = useInitiatePayment();

	const onSubmit: SubmitHandler<InvoicePaymentDto> = (data) => {
		setUploading(true);

		initiatePayment(
			{
				payload: {
					...data,
					reference: reference!,
				},
			},
			{
				onSuccess: (data) => {
					const link = document.createElement('a');
					link.href = data.data.checkoutUrl as string;
					link.target = "_blank";
					link.click();
					setUploading(false);
				}
			}
		);
	};

	return (
		<CompanySetupLayout pathname={window.location.pathname}>
			<Helmet>
				<title>Subscription Payment | Akatua</title>
			</Helmet>
			{hasPermission && (
				<>
				{!subscriptionStatus && (
					<div>
						{/* Back button */}
						<div
							onClick={() => {
								navigate(PATHS.CREATE_PAYROLL_COMPANY + "?displayPackages=true");
							}}
							className="flex justify-center w-full mb-12 cursor-pointer"
						>
							<div className="flex text-primary-blue w-[1160px] items-center text-2sm space-x-2">
								<IoChevronBackOutline className="text-sm" />
								<p>Go Back</p>
							</div>
						</div>
						<div className="flex justify-center items-start space-x-6">
							{/* Subscription form */}
							<form 
								onSubmit={handleSubmit(onSubmit)}
								className="w-[560px] bg-[#26A0F91A] rounded-lg p-8"
							>
								<h3 className="text-2xs">Subscribe to</h3>
								<h1 className="font-bold text-2xl mb-8">{subscriptionTier?.name}</h1>
								<div className="space-y-6">
									<div className="flex w-full justify-between items-center">
										<div className="space-y-1">
											<h3 className="font-bold text-base">Subscription</h3>
											<p className="text-2xs text-charcoal">Billed annually</p>
										</div>
										<p className="text-primary-blue text-lg font-bold">
											{Number(subscriptionTier?.priceAmount) === 0
												? "FREE"
												: getPackageBillingStats(subscriptionTier)
											}
										</p>
									</div>
									<div className="w-full space-y-2">
										<h3 className="font-bold text-base">Payment Type</h3>
										<div>
											<div className="flex items-center gap-7 accent-[#0b49761a]">
												<div className="flex items-center gap-3">
													<input 
														type="radio" 
														id="channelCodeCard" 
														{...register("channelCode", {
															required: "Select payment type here",
														})}
														value="CARD"
														onInput={() => setValue("channelCode", "CARD")}
													/>
													<label htmlFor="channelCodeCard">Card</label>
												</div>
												<div className="flex items-center gap-3">
													<input 
														type="radio" 
														id="channelCodeWallet" 
														{...register("channelCode", {
															required: "Select payment type here",
														})}
														value="MOBILE_MONEY"
													/>
													<label htmlFor="channelCodeWallet">Mobile Money</label>
												</div>
											</div>
											{errors.channelCode && (
												<p className="text-red-500 text-sm mt-1">
													{errors.channelCode.message}
												</p>
											)}
										</div>
									</div>
									{channelCode && (
										<div className="w-full space-y-2">
											<h3 className="font-bold text-base">
												{isCardPayment ? "Bank" : "Wallet"}
											</h3>
											<div>
												<select
													className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
														errors.network
															? " border border-red-500 focus:border-red-500 focus:outline-red-500"
															: "border-none"
													}`}
													{...register("network", {
														required: isCardPayment
															? "Select bank here"
															: "Select wallet here",
													})}
												>
													<option value="">
														{isCardPayment ? "Select Bank" : "Select Wallet"}
													</option>
													{paymentInstitutions?.data?.map((item) => (
														<option key={item.id} value={item.shortName}>
															{item.name}
														</option>
													))}
												</select>
												{errors.network && (
													<p className="text-red-500 text-sm mt-1">
														{errors.network.message}
													</p>
												)}
											</div>
										</div>
									)}
									<div className="w-full space-y-2">
										<h3 className="font-bold text-base">Telephone</h3>
										<div>
											<Controller
												name={"msisdn"}
												control={control}
												rules={{
													required: false,
													maxLength: {
														value: 12,
														message: "Enter a valid phone number",
													},
												}}
												render={({ field }) => (
													<div
														className={`w-full bg-secondary-gray rounded-lg mt-2 flex items-center ${
															errors.msisdn
																? "border border-red-500 focus:border-red-500 focus:outline-red-500"
																: "border-none"
														}`}
													>
														<PhoneInput
															{...field}
															country={"gh"}
															disabled={!isEditable}
															inputStyle={{
																height: errors.msisdn ? "50px" : "48px",
																border: "none",
																backgroundColor: "#F2F2F4",
																color: "black",
																boxShadow: "none",
																width: "95%",
																margin: "0px 0px 0px 15px",
																borderRadius: "10px",
															}}
															containerStyle={{
																// marginTop: "8px",
																height: errors.msisdn ? "50px" : "48px",
																border: "none",
																backgroundColor: "#F2F2F4",
																boxShadow: "none",
																borderRadius: "10px",
															}}
															buttonStyle={{
																border: "none",
																backgroundColor: "#F2F2F4",
																boxShadow: "none",
																padding: "0px 10px",
															}}
														/>
													</div>
												)}
											/>
											{errors.msisdn && (
												<p className="text-red-500 text-sm mt-1">
													{errors.msisdn.message}
												</p>
											)}
										</div>
									</div>
									<div className="border-secondary-darkgray h-[1px] border-b w-full" />
								</div>
								<div className="flex justify-center mt-14">
									<OrangeButton
										type="submit"
										title={"Subscribe"}
										className={"px-14"}
										disabled={uploading}
									/>
								</div>
							</form>
						</div>
					</div>
				)}
				{subscriptionStatus === "SUCCESS" && (
					<div className="mt-40 flex flex-col justify-center items-center text-sm">
						<div className="text-primary-blue bg-green-50 rounded-full flex justify-center items-center h-20 w-20 text-4xl">
							💫
						</div>
						<h1 className="text-2xl font-bold mt-10">
							Thank you for Subscribing!
						</h1>
						<p className="w-[458px] text-center mt-4">
							You have successfully subscribed to the{" "}
							<span className="font-medium">{subscriptionTier?.name} Package</span>. Start
							creating your companies
						</p>
						<div className="w-[15%] mt-8">
							<OrangeButton
								title={"Continue"}
								onClick={() => navigate(PATHS.COMPANY_ACCOUNT_SETUP)}
								className={"w-full"}
							/>
						</div>
					</div>
				)}
				{subscriptionStatus === "FAILED" && (
					<div className="mt-48 flex flex-col justify-center items-center text-sm">
						<div className="text-primary-blue bg-red-50 rounded-full flex justify-center items-center h-20 w-20 text-5xl">
							❗
						</div>
						<h1 className="text-2xl font-bold mt-10">Something went wrong!</h1>
						<p className="w-[400px] text-center mt-4">
							Sorry, you were unable to subscribe. Please contact support or Try
							again
						</p>
					</div>
				)}
				</>
			)}
		</CompanySetupLayout>
	);
};

export default SubscriptionPayment;

const getPackageBillingStats = (subscriptionTierPackage?: DetailedSubscriptionTierDto) => {
	const currencyCode = subscriptionTierPackage?.currency?.code || "$";
	const priceAmount = moneyFormat(subscriptionTierPackage?.priceAmount || "");

	switch(subscriptionTierPackage?.billingPeriod) {
		case "MONTHLY":
			return currencyCode + " " + priceAmount + "/mo"
		case "ANNUALLY":
			return currencyCode + " " + priceAmount + "/yr"
		default:
			return currencyCode + " " + priceAmount
	}
};
